"use client";

import Image from "next/image";
import Link from "next/link";
import { Menu, UserRound, X } from "lucide-react";
import { useState } from "react";

import type { AgencyNavigationItem } from "@/app/lib/fetchDynamicPage";
import AddressInput from "../design-system/molecules/AddressInput";

type AgencyHeaderProps = {
  navigation: AgencyNavigationItem[];
};

export default function AgencyHeader({ navigation }: AgencyHeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-[100] isolate bg-white shadow-sm">

    {/* ================= DESKTOP ================= */}
      <div className="mx-auto hidden max-w-[1400px] px-6 py-4 lg:block">

        {/* TOP BAR */}
        <div className="flex items-center justify-end gap-3">
          <Link
            href="https://clients.les-menus-services.com/"
            className="flex h-[44px] shrink-0 items-center gap-2 rounded-md bg-greenPastel px-4 text-sm font-medium text-textColor"
          >
            <UserRound size={19} />
            <span>Mon compte</span>
          </Link>

          <div className="w-[360px] max-w-full">
            <AddressInput />
          </div>
        </div>

        {/* LOGO + NAVIGATION */}
        <div className="flex items-center 2xl:justify-between justify-center gap-60 2xl:gap-0">
          <Link href="https://www.les-menus-services.com/" className="shrink-0 ">
            <Image
              src="https://fra1.digitaloceanspaces.com/site-lms/logo_LMS_sans_Bas_1_25b1d5b4cc.svg"
              alt="Les Menus Services"
              width={90}
              height={90}
              className="h-auto w-[75px] "
              priority
            />
          </Link>

          <nav aria-label="Navigation agence">
            <ul className="flex items-center gap-7">
              {navigation.map((item) => (
                <li key={item.zone}>
                  <Link
                    href={item.href}
                    className="whitespace-nowrap font-medium text-textColor transition-colors hover:text-primaryGreen"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>


      {/* ================= MOBILE / TABLETTE ================= */}
      <div className="lg:hidden">

        {/* LOGO + PROFIL + BURGER */}
        <div className="flex items-center justify-between px-5 pb-5 pt-4">
          <Link href="https://www.les-menus-services.com/" className="shrink-0">
            <Image
              src="https://fra1.digitaloceanspaces.com/site-lms/logo_LMS_sans_Bas_1_25b1d5b4cc.svg"
              alt="Les Menus Services"
              width={90}
              height={90}
              priority
              className="h-auto w-[88px]"
            />
          </Link>

          <div className="flex items-center gap-4">

            {/* PROFIL */}
            <Link
              href="https://clients.les-menus-services.com/"
              aria-label="Mon compte"
              className="flex h-[44px] w-[44px] items-center justify-center text-textColor"
            >
              <UserRound size={27} strokeWidth={1.5} />
            </Link>

            {/* BURGER */}
            <button
              type="button"
              aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className="flex h-[48px] w-[48px] items-center justify-center rounded-full bg-greenPastel text-primaryGreen"
            >
              {isMobileMenuOpen ? (
                <X size={28} strokeWidth={2} />
              ) : (
                <Menu size={30} strokeWidth={2} />
              )}
            </button>

          </div>
        </div>

        {/* RECHERCHE ADRESSE TOUJOURS VISIBLE */}
        <div className="px-5 pb-5">
          <AddressInput />
        </div>

        {/* MENU MOBILE */}
        {isMobileMenuOpen && (
          <nav
            aria-label="Navigation agence mobile"
            className="border-t border-gray-200 bg-white px-5 py-5 shadow-lg"
          >
            <ul className="flex flex-col">
              {navigation.map((item) => (
                <li key={item.zone} className="border-b border-gray-100 last:border-none">
                  <Link
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-4 font-medium text-textColor transition-colors hover:text-primaryGreen"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}

      </div>
    </header>
  );
}